import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import Profile2User from 'iconsax-react/dist/esm/Profile2User';
import { TDR } from 'tdr-common';
import { AvailabilityGuardContext, SearchFilters, serializeSearchFiltersForURL } from '../../routes/AvailabilityGuard';
import { AnnouncementResponse } from '../announcements/common';
import { CardShell } from './CardShell';
import { TableCategoryCounts } from './categorizeTables';
import { emitTableClicked } from './cardAnalytics';
import { analytics } from '../../helpers/analytics';
import { RESULT_CLICKED } from '../../helpers/analytics/constants';

type CrossSellGroupSizeCardProps = {
    restaurant: TDR.Restaurant;
    table: TDR.Table;
    searchFilters: SearchFilters;
    announcement?: AnnouncementResponse;
    viewedTable?: boolean;
    counts: TableCategoryCounts;
    totalResults: number;
    position: number;
    hasScrolledToBottom?: boolean;
    timeslotData?: TDR.TimeSlotMap;
};

const Footer = ({ onClick }: { onClick: () => void }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-tablz-med-gray-1 flex items-center justify-between p-3 rounded-2xl text-sm">
            <div className="flex gap-1 items-center">
                <Profile2User size={16} color="#FACC15" variant="Bold" />
                <p className="font-poppins text-yellow-400">
                    <Trans
                        i18nKey="inventory.available_for_other_group_sizes"
                        components={[<span key="b" className="font-semibold" />]}
                    />
                </p>
            </div>
            <button
                type="button"
                className="font-poppins font-semibold text-[color:var(--zinc-200,#e4e4e7)] cursor-pointer"
                onClick={onClick}
                data-testid="see-details-btn"
            >
                {t('inventory.see_details')}
            </button>
        </div>
    );
};

export const CrossSellGroupSizeCard = ({
    restaurant, table, searchFilters, announcement, viewedTable,
    counts, totalResults, position, hasScrolledToBottom, timeslotData
}: CrossSellGroupSizeCardProps) => {
    const navigate = useNavigate();
    const { setPreviousTime, setPreviousDate } = useOutletContext<AvailabilityGuardContext>();

    const handleClick = () => {
        setPreviousTime(searchFilters.time || '');
        setPreviousDate(searchFilters.date || '');
        analytics.track(RESULT_CLICKED, { resultType: 'table group size cross sell' });
        emitTableClicked({
            table, restaurantId: restaurant.id, searchFilters, counts, totalResults,
            position, tableStatus: 'groupsize_conflict', announcement, hasScrolledToBottom
        });
        navigate({
            pathname: table.slug,
            search: serializeSearchFiltersForURL({ ...searchFilters, groupSize: undefined }).toString()
        });
    };

    return (
        <CardShell
            restaurant={restaurant}
            table={table}
            searchFilters={searchFilters}
            announcement={announcement}
            viewedTable={viewedTable}
            soldOutOverlay={false}
            onCardClick={handleClick}
            timeslotData={timeslotData}
            footer={<Footer onClick={handleClick} />}
        />
    );
};
